'use client';

import { enableGuarantee } from '@castlery/config';
import { AccordionGroup, Box, Sheet, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import {
  selectCurrentBundleVariants,
  selectProduct,
  selectVariant,
  selectWarrantyList,
} from '@castlery/modules-product-domain';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import { useCallback, useMemo } from 'react';
import { ProductHullabala } from './components/product-hullabala';
import { ProductInfoItem } from './components/product-info-item';
import { EVENT_PDP_DETAILS } from '@castlery/modules-tracking-services';

interface ProductInfoClientProps {
  pageType: 'pdp' | 'pla';
  children: React.ReactNode;
}

export const ProductInfoClient = (props: ProductInfoClientProps) => {
  const { pageType, children } = props;
  const dispatch = useAppDispatch();

  const product = useAppSelector(selectProduct);
  const variant = useAppSelector(selectVariant);
  const { mobile, tablet } = useBreakpoints();
  const warrantyList = useAppSelector(selectWarrantyList);
  const currentBundleVariant = useAppSelector(selectCurrentBundleVariants);
  const selectVariantId = useMemo(() => {
    if (product?.product_type === 'bundle') {
      return currentBundleVariant?.[0]?.id;
    }
    return variant?.id;
  }, [currentBundleVariant, product?.product_type, variant]);

  const description = useMemo(() => {
    return product?.description && product?.description?.replace(/\u00a0/g, ' ');
  }, [product]);

  const handleTrackPDPDetails = useCallback(async ({ action, label }: { action: string; label: string }) => {
    await dispatch(
      EVENT_PDP_DETAILS({
        action: action,
        label: label,
      })
    );
  }, []);

  const handleAccordionChange = useCallback(async (action: string, expand: boolean) => {
    handleTrackPDPDetails({ action: action, label: expand ? 'expand' : 'close' });
  }, []);

  return (
    <Stack px={mobile ? 6 : tablet ? 6 : undefined} mb={7}>
      {pageType !== 'pla' && (
        <Sheet variant="solid" sx={{ mt: 4, mb: 4, py: mobile ? 4 : 6, px: 4 }}>
          <Stack>
            <Typography level="body2" sx={{ mb: 2, color: 'var(--fortress-palette-neutral-plainColor)' }}>
              {description}
            </Typography>
            <Box
              onClick={(e) => {
                e.preventDefault();
                handleTrackPDPDetails({ action: 'view_collection', label: 'click' });
              }}
            >
              {children}
            </Box>
          </Stack>
        </Sheet>
      )}
      {pageType !== 'pla' && selectVariantId && <ProductHullabala />}
      <Box
        sx={(theme) => ({
          height: theme.spacing(4),
        })}
      />
      <AccordionGroup>
        <ProductInfoItem
          header="Material"
          action="details"
          propertyName="product_details"
          product={product}
          variant={variant}
          key="Material"
          onAccordionChange={(expand) => handleAccordionChange('details', expand)}
          // expandByDefault={!plaCollapsed}
        />
        <ProductInfoItem
          header="Dimensions"
          action="dimensions"
          propertyName="product_dimensions"
          product={product}
          variant={variant}
          key="Dimensions"
          onAccordionChange={(expand) => handleAccordionChange('dimensions', expand)}
          // expandByDefault={dimensionExpand}
        />
        <ProductInfoItem
          header={enableGuarantee ? 'Delivery and Guarantee' : 'Delivery and warranty'}
          action="delivery"
          propertyName="delivery_returns"
          warrantyList={warrantyList}
          product={product}
          variant={variant}
          key="Delivery"
          onAccordionChange={(expand) => handleAccordionChange('delivery', expand)}
        />
      </AccordionGroup>
    </Stack>
  );
};
